import API from '../../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Card, Table } from 'react-bootstrap';

import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer
} from 'recharts';
/* ---------------------------------------------------------------------------
   Chart palette — validated for colour-vision deficiency against the white
   card surface (worst all-pairs CVD ΔE 9.2, normal-vision ΔE 24.0). Each chart
   carries ONE series, so every mark in a chart shares one hue; bar length is
   never double-encoded as colour.
   The aqua sits just under 3:1 on white, so those charts always ship visible
   value labels and a table view.
--------------------------------------------------------------------------- */
const SERIES = {
  blue: '#2a78d6',
  aqua: '#1baf7a',
  orange: '#eb6834'
};

const INK = {
  grid: '#e1e0d9',
  muted: '#898781',
  secondary: '#52514e',
  primary: '#0b0b0b'
};

const MONTH_ORDER = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/* Full Indian-format rupees, for tooltips and tables */
const rupees = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

/* Compact rupees for axis ticks, so the axis never crowds */
const rupeesShort = (value) => {
  const n = Number(value || 0);

  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}k`;

  return `₹${n}`;
};

const truncate = (text, max) =>
  String(text || '').length > max
    ? `${String(text).slice(0, max - 1)}…`
    : String(text || '');

/* Shared tooltip — one row, label above value, never the only way to read a
   number (every chart also has a table view). */
function ChartTooltip({ active, payload, label, format }) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <div className="sch-tip">
      <div className="sch-tip-label">{label}</div>

      <div className="sch-tip-value">
        <span
          className="sch-tip-dot"
          style={{ background: payload[0].color || payload[0].fill }}
        />
        {format(payload[0].value)}
      </div>
    </div>
  );
}

/*
  Card shell: header, chart/table toggle, loading and empty states.
  `renderChart` is only called once there is data to draw.
*/
function ChartCard({
  icon,
  title,
  hint,
  summary,
  loading,
  data,
  columns,
  emptyText,
  children
}) {
  const [view, setView] = useState('chart');

  const hasData = Array.isArray(data) && data.length > 0;

  return (
    <Card className="border-0 mb-5 sch-card">
      <Card.Body className="p-3 p-md-4">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-1">
          <div className="d-flex align-items-center gap-3">
            <span className="sch-icon">{icon}</span>

            <div>
              <h5 className="sch-title">{title}</h5>
              {hint && <small className="sch-hint">{hint}</small>}
            </div>
          </div>

          <div className="d-flex align-items-center gap-3">
            {summary && !loading && hasData && (
              <div className="text-end sch-summary">
                <div className="sch-summary-value">{summary.value}</div>
                <div className="sch-summary-label">{summary.label}</div>
              </div>
            )}

            {hasData && !loading && (
              <div className="sch-toggle" role="group">
                <button
                  type="button"
                  className={view === 'chart' ? 'is-on' : ''}
                  onClick={() => setView('chart')}
                >
                  Chart
                </button>
                <button
                  type="button"
                  className={view === 'table' ? 'is-on' : ''}
                  onClick={() => setView('table')}
                >
                  Table
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="sch-rule" />

        {loading ? (
          <div className="sch-skel-wrap">
            <div className="sch-skel sch-skel-plot" />
          </div>
        ) : !hasData ? (
          <div className="sch-empty">
            <div className="sch-empty-icon">📭</div>
            <div className="fw-bold">No data yet</div>
            <small className="text-muted">{emptyText}</small>
          </div>
        ) : view === 'chart' ? (
          children
        ) : (
          <div className="sch-table-wrap">
            <Table className="sch-table mb-0" hover size="sm">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={col.align === 'right' ? 'text-end' : ''}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={col.align === 'right' ? 'text-end' : ''}
                      >
                        {col.format ? col.format(row[col.key]) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

/* Sales, category revenue and top-selling product charts. */
function SalesCharts() {
  const [salesData, setSalesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async (url, setter) => {
      try {
        const { data } = await axios.get(url);
        setter(data);
      } catch (error) {
        console.log(error);
      }
    };

    Promise.all([
      load(`${API}/admin/sales-analytics`, setSalesData),
      load(`${API}/admin/category-analytics`, setCategoryData),
      load(`${API}/admin/top-products`, setTopProducts)
    ]).finally(() => setLoading(false));
  }, []);

  /* The API builds months from object keys, so they arrive in insertion order.
     A time axis has to run chronologically or the line implies a trend that
     isn't there. */
  const sales = [...salesData].sort(
    (a, b) => MONTH_ORDER.indexOf(a.month) - MONTH_ORDER.indexOf(b.month)
  );

  /* Biggest first — a ranked bar chart reads top-down */
  const categories = [...categoryData].sort(
    (a, b) => (b.revenue || 0) - (a.revenue || 0)
  );

  const products = [...topProducts].sort(
    (a, b) => (b.sales || 0) - (a.sales || 0)
  );

  const totalSales = sales.reduce((sum, row) => sum + (row.sales || 0), 0);

  const bestCategory = categories[0];

  const totalUnits = products.reduce((sum, row) => sum + (row.sales || 0), 0);

  /* Month-over-month change on the last two points */
  const delta =
    sales.length >= 2
      ? (() => {
          const prev = sales[sales.length - 2].sales || 0;
          const last = sales[sales.length - 1].sales || 0;

          if (prev === 0) return null;

          return ((last - prev) / prev) * 100;
        })()
      : null;

  /* Height grows with the number of rows so long lists never squash */
  const productsHeight = Math.max(260, products.length * 38 + 60);

  return (
    <>
      <style>{salesChartStyles}</style>

      {/* ===== Monthly sales ===== */}
      <ChartCard
        icon="📈"
        title="Monthly Sales"
        hint="Revenue collected each month"
        summary={{ value: rupees(totalSales), label: 'Total' }}
        loading={loading}
        data={sales}
        emptyText="Sales will appear here once orders start coming in."
        columns={[
          { key: 'month', label: 'Month' },
          {
            key: 'sales',
            label: 'Revenue',
            align: 'right',
            format: rupees
          }
        ]}
      >
        {delta !== null && (
          <div className={`sch-delta ${delta >= 0 ? 'is-up' : 'is-down'}`}>
            {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}% vs last month
          </div>
        )}

        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={sales}
            margin={{ top: 24, right: 24, left: 4, bottom: 4 }}
          >
            <defs>
              <linearGradient id="schSalesFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={SERIES.blue} stopOpacity={0.26} />
                <stop offset="100%" stopColor={SERIES.blue} stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid
              stroke={INK.grid}
              strokeWidth={1}
              vertical={false}
            />

            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={{ stroke: INK.grid }}
              tick={{ fill: INK.muted, fontSize: 12 }}
              dy={6}
            />

            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: INK.muted, fontSize: 12 }}
              tickFormatter={rupeesShort}
              width={62}
            />

            <Tooltip
              cursor={{ stroke: INK.grid, strokeWidth: 1 }}
              content={<ChartTooltip format={rupees} />}
            />

            <Area
              type="monotone"
              dataKey="sales"
              stroke={SERIES.blue}
              strokeWidth={2}
              fill="url(#schSalesFill)"
              /* 10px marker with a 2px surface ring, per mark spec */
              activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
              dot={false}
              /* recharts 3 defers LabelList until the mark's entrance
                 animation finishes; the value labels are required reading,
                 so the marks render immediately instead. */
              isAnimationActive={false}
            >
              {/* Label only the endpoint — never a number on every point */}
              <LabelList
                dataKey="sales"
                position="top"
                content={({ x, y, value, index }) =>
                  index === sales.length - 1 ? (
                    <text
                      x={x}
                      y={y - 12}
                      textAnchor="middle"
                      fill={INK.primary}
                      fontSize={12}
                      fontWeight={700}
                    >
                      {rupeesShort(value)}
                    </text>
                  ) : null
                }
              />
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ===== Category revenue ===== */}
      <ChartCard
        icon="📊"
        title="Revenue by Category"
        hint="Which categories bring the money in"
        summary={
          bestCategory
            ? { value: bestCategory.category, label: 'Top category' }
            : null
        }
        loading={loading}
        data={categories}
        emptyText="Add categories and products to see revenue split."
        columns={[
          { key: 'category', label: 'Category' },
          {
            key: 'revenue',
            label: 'Revenue',
            align: 'right',
            format: rupees
          }
        ]}
      >
        <ResponsiveContainer width="100%" height={340}>
          <BarChart
            data={categories}
            margin={{ top: 26, right: 16, left: 4, bottom: 4 }}
            barCategoryGap="28%"
          >
            <CartesianGrid
              stroke={INK.grid}
              strokeWidth={1}
              vertical={false}
            />

            <XAxis
              dataKey="category"
              tickLine={false}
              axisLine={{ stroke: INK.grid }}
              tick={{ fill: INK.muted, fontSize: 12 }}
              tickFormatter={(value) => truncate(value, 14)}
              interval={0}
              dy={6}
            />

            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: INK.muted, fontSize: 12 }}
              tickFormatter={rupeesShort}
              width={62}
            />

            <Tooltip
              cursor={{ fill: 'rgba(15,23,42,0.04)' }}
              content={<ChartTooltip format={rupees} />}
            />

            <Bar
              dataKey="revenue"
              fill={SERIES.aqua}
              radius={[4, 4, 0, 0]}
              maxBarSize={54}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="revenue"
                position="top"
                offset={8}
                fill={INK.secondary}
                fontSize={11}
                fontWeight={600}
                formatter={rupeesShort}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ===== Top selling products ===== */}
      <ChartCard
        icon="🔥"
        title="Top Selling Products"
        hint="Ranked by units sold"
        summary={{ value: totalUnits.toLocaleString('en-IN'), label: 'Units' }}
        loading={loading}
        data={products}
        emptyText="Once products start selling, your bestsellers land here."
        columns={[
          { key: 'name', label: 'Product' },
          {
            key: 'sales',
            label: 'Units Sold',
            align: 'right',
            format: (value) => Number(value || 0).toLocaleString('en-IN')
          }
        ]}
      >
        <ResponsiveContainer width="100%" height={productsHeight}>
          <BarChart
            data={products}
            layout="vertical"
            margin={{ top: 4, right: 44, left: 4, bottom: 4 }}
            barCategoryGap="26%"
          >
            <CartesianGrid
              stroke={INK.grid}
              strokeWidth={1}
              horizontal={false}
            />

            <XAxis
              type="number"
              tickLine={false}
              axisLine={{ stroke: INK.grid }}
              tick={{ fill: INK.muted, fontSize: 12 }}
              allowDecimals={false}
            />

            <YAxis
              dataKey="name"
              type="category"
              tickLine={false}
              axisLine={false}
              tick={{ fill: INK.secondary, fontSize: 12 }}
              tickFormatter={(value) => truncate(value, 24)}
              width={170}
              interval={0}
            />

            <Tooltip
              cursor={{ fill: 'rgba(15,23,42,0.04)' }}
              content={
                <ChartTooltip
                  format={(value) =>
                    `${Number(value || 0).toLocaleString('en-IN')} units`
                  }
                />
              }
            />

            <Bar
              dataKey="sales"
              fill={SERIES.orange}
              radius={[0, 4, 4, 0]}
              maxBarSize={26}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="sales"
                position="right"
                offset={8}
                fill={INK.secondary}
                fontSize={11}
                fontWeight={700}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </>
  );
}

const salesChartStyles = `
  .sch-card {
    border-radius: 20px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(124, 45, 18, 0.08);
    transition: box-shadow 0.25s ease;
  }
  .sch-card:hover {
    box-shadow: 0 16px 34px rgba(124, 45, 18, 0.13);
  }

  /* Header */
  .sch-icon {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    border-radius: 14px;
    background: linear-gradient(135deg, #fff7ed, #ffedd5);
    border: 1px solid #fed7aa;
  }
  .sch-title {
    font-weight: 800;
    margin: 0;
    font-size: 1.05rem;
    color: #7c2d12;
  }
  .sch-hint {
    color: #a8a29e;
    font-size: 0.76rem;
  }
  .sch-summary-value {
    font-weight: 800;
    font-size: 1.05rem;
    color: #0b0b0b;
    line-height: 1.2;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sch-summary-label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #a8a29e;
  }

  /* Chart / table switch */
  .sch-toggle {
    display: inline-flex;
    background: #f5f3f0;
    border-radius: 30px;
    padding: 3px;
  }
  .sch-toggle button {
    border: none;
    background: transparent;
    color: #78716c;
    font-size: 0.76rem;
    font-weight: 700;
    padding: 5px 14px;
    border-radius: 30px;
    transition: background 0.2s ease, color 0.2s ease;
  }
  .sch-toggle button:hover { color: #7c2d12; }
  .sch-toggle button.is-on {
    background: #fff;
    color: #c2410c;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.12);
  }

  .sch-rule {
    height: 1px;
    background: linear-gradient(90deg, #fed7aa, transparent);
    margin: 14px 0 6px;
  }

  /* Month-over-month pill */
  .sch-delta {
    display: inline-block;
    font-size: 0.74rem;
    font-weight: 700;
    border-radius: 30px;
    padding: 4px 12px;
    margin-bottom: 4px;
  }
  .sch-delta.is-up   { color: #006300; background: #f0fdf4; border: 1px solid #bbf7d0; }
  .sch-delta.is-down { color: #b91c1c; background: #fef2f2; border: 1px solid #fecaca; }

  /* Tooltip */
  .sch-tip {
    background: #fff;
    border: 1px solid rgba(11, 11, 11, 0.10);
    border-radius: 12px;
    padding: 9px 13px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.16);
  }
  .sch-tip-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #898781;
    margin-bottom: 3px;
  }
  .sch-tip-value {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 0.95rem;
    font-weight: 800;
    color: #0b0b0b;
  }
  .sch-tip-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  /* Table view */
  .sch-table-wrap {
    max-height: 340px;
    overflow-y: auto;
    margin-top: 10px;
  }
  .sch-table {
    font-size: 0.88rem;
  }
  .sch-table thead th {
    position: sticky;
    top: 0;
    background: #faf7f2;
    color: #78716c;
    font-size: 0.68rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #f1e4d0;
  }
  .sch-table tbody td {
    color: #44403c;
    vertical-align: middle;
  }
  /* Numbers align down the column */
  .sch-table td.text-end, .sch-table th.text-end {
    font-variant-numeric: tabular-nums;
  }

  /* Empty + loading */
  .sch-empty {
    text-align: center;
    padding: 46px 16px;
    color: #78716c;
  }
  .sch-empty-icon {
    font-size: 2.6rem;
    margin-bottom: 6px;
  }
  .sch-skel-wrap { padding-top: 12px; }
  .sch-skel {
    display: block;
    border-radius: 12px;
    background: linear-gradient(90deg, #f1ece4 25%, #faf7f2 37%, #f1ece4 63%);
    background-size: 400% 100%;
    animation: schShimmer 1.4s ease infinite;
  }
  .sch-skel-plot { height: 280px; }
  @keyframes schShimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  /* Recharts focus outline is distracting on a static plot */
  .sch-card .recharts-wrapper:focus,
  .sch-card .recharts-surface:focus { outline: none; }

  @media (max-width: 575.98px) {
    .sch-icon { width: 36px; height: 36px; font-size: 1rem; }
    .sch-title { font-size: 0.95rem; }
    .sch-summary-value { font-size: 0.9rem; max-width: 130px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .sch-card, .sch-toggle button, .sch-skel { transition: none; animation: none; }
  }
`;

export default SalesCharts;
